import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom'; // ✅ NEW

const Footer = ({ scrollToSection }) => {
  const navItems = [
    { name: 'Home', id: 'home' },
    { name: 'About', id: 'about' },
    { name: 'Products', id: 'products' },
    { name: 'Sample Room', id: 'sample-room' },
    { name: 'Brands', id: 'brands' },
    { name: 'Our Clients', id: 'clients' },
    { name: 'Contact', id: 'contact' },
  ];

  return (
    <footer className="bg-gray-900 text-white pt-16 pb-8 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent"></div>
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 md:px-6 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          {/* Company Info - lg:col-span-4 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-4"
          >
            <div className="space-y-6">
              <div>
                <h3 className="text-3xl font-playfair font-bold mb-2">
                  Sudharma<span className="text-amber-400">.</span>
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Since 1999, we've been crafting premium garments with passion,
                  precision, and innovation. Trusted by leading brands worldwide.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPin className="text-amber-400 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">
                    R.s grewal road, Street No. 7, Band Bhadur Nagar, Bahadarke,
                    Ludhiana, Punjab 141008
                  </span>
                </div>
                <div className="flex items-center">
                  <Phone className="text-amber-400 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">+91 84370 44525</span>
                </div>
                <div className="flex items-center">
                  <Mail className="text-amber-400 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Scc525@hotmail.com</span>
                </div>

                {/* ✅ GST Highlighted */}
                <div className="flex items-center">
                  <span className="text-amber-400 mr-3 flex-shrink-0 font-semibold">
                    GST
                  </span>
                  <span className="text-gray-100 font-semibold tracking-wide">
                    03AANCS0404Q1Z8
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Quick Links - lg:col-span-3 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-3"
          >
            <div className="space-y-6">
              <h4 className="text-xl font-semibold text-white mb-6 pb-2 border-b border-amber-700/50">
                Quick Links
              </h4>
              <ul className="space-y-3">
                {navItems.map((item) => (
                  <motion.li
                    key={item.id}
                    whileHover={{ x: 5 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className="text-gray-300 hover:text-amber-400 transition-colors flex items-center group"
                    >
                      <ArrowUpRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      {item.name}
                    </button>
                  </motion.li>
                ))}

                {/* ✅ New Policies link */}
                <motion.li
                  whileHover={{ x: 5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <Link
                    to="/privacy-policy"
                    className="text-gray-300 hover:text-amber-400 transition-colors flex items-center group"
                  >
                    <ArrowUpRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    Policy
                  </Link>
                </motion.li>
              </ul>
            </div>
          </motion.div>

          {/* Our Location / Map - lg:col-span-5 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-amber-700/50">
                Our Location
              </h4>
              <p className="text-gray-300 text-sm md:text-base">
                Visit our manufacturing unit and office at our Ludhiana facility.
              </p>

              {/* Map embed */}
              <div className="relative w-full h-64 md:h-80 rounded-xl overflow-hidden border border-gray-800 shadow-lg">
                <iframe
                  title="Sudharma Clothing Location"
                  src="https://www.google.com/maps?q=Sudharma%20Clothing%20Company%20Ahmedabad&output=embed"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-full border-0"
                  allowFullScreen
                ></iframe>
              </div>

              <div className="pt-2">
                <a
                  href="https://maps.app.goo.gl/tybU2htYWmL9k6HEA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-amber-400 hover:text-amber-300 text-sm md:text-base font-medium transition-colors"
                >
                  Open in Google Maps
                  <ArrowUpRight className="w-4 h-4 ml-1" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col items-center">
            <div className="text-gray-400 text-sm text-center">
              &copy; {new Date().getFullYear()} Sudharma Clothing Company Pvt. Ltd. All rights reserved.
            </div>
          </div>

          <div className="mt-6 text-center text-xs text-gray-500">
            <p>
              Designed with ❤️{' '}
              <span className="font-bold">Samyak Jain</span>{' '}
              for Sudharma Clothing Company Pvt. Ltd.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
